'use client';

import { useState, useEffect, use } from 'react';
import { useRegionalFormatters } from '@/lib/use-regional-formatters';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { Search, Calendar, Clock, User, Tag, TrendingUp, Sparkles, ArrowRight } from 'lucide-react';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  featured_image: string;
  author_name: string;
  category_name: string;
  published_at: string;
  tags: Array<{ id: number; name: string; slug: string }>;
}

export default function BlogPage({ params }: { params: Promise<{ locale: string }> }) {
  const { locale } = use(params);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [categories, setCategories] = useState<any[]>([]);
  const searchParams = useSearchParams();
  const router = useRouter();
  
  const page = searchParams.get('page') || '1';
  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [page, category, search]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        status: 'published',
        ...(category && { category }),
        ...(search && { search })
      });
      
      const res = await fetch(`/api/blog/posts?${params}`);
      const data = await res.json();
      
      if (data.success) {
        setPosts(data.data.posts);
        setPagination(data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch('/api/blog/categories');
      const data = await res.json();
      if (data.success) setCategories(data.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const searchValue = formData.get('search') as string;
    const params = new URLSearchParams(searchParams.toString());
    if (searchValue) {
      params.set('search', searchValue);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    router.push(`?${params.toString()}`);
  };

  const filterByCategory = (slug: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (slug) {
      params.set('category', slug);
    } else {
      params.delete('category');
    }
    params.set('page', '1');
    router.push(`?${params.toString()}`);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-950 dark:via-blue-950/30 dark:to-indigo-950/20">
      {/* Animated background orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob" />
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob animation-delay-2000" />
      <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-gradient-to-r from-green-400 to-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob animation-delay-4000" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Hero Header */}
        <div className="text-center mb-16 space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-indigo-500/10 backdrop-blur-xl border border-white/20 dark:border-white/10">
            <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
              Latest Insights
            </span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-black bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 dark:from-white dark:via-blue-100 dark:to-indigo-100 bg-clip-text text-transparent leading-tight">
            Blog
          </h1>
          
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Cutting-edge insights, best practices, and industry trends in backup management
          </p>
        </div>

        {/* Search Bar - Glass Morphism */}
        <div className="mb-12 max-w-3xl mx-auto">
          <form onSubmit={handleSearch}>
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-pink-600 rounded-xl blur-lg opacity-25 group-hover:opacity-40 transition duration-500" />
              <div className="relative flex items-center">
                <Search className="absolute left-6 h-5 w-5 text-slate-400 dark:text-slate-500" />
                <input
                  type="text"
                  name="search"
                  defaultValue={search}
                  placeholder="Search articles, topics, or keywords..."
                  className="w-full pl-14 pr-6 py-5 rounded-xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-white/20 dark:border-slate-700/50 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                />
              </div>
            </div>
          </form>
        </div>

        {/* Category Pills */}
        <div className="mb-16 flex flex-wrap gap-3 justify-center">
          <button
            onClick={() => filterByCategory('')}
            className={`group relative px-6 py-3 rounded-full font-semibold transition-all duration-200 ${
              !category
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/50'
                : 'bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border border-white/20 dark:border-slate-700/50 text-slate-700 dark:text-slate-300 '
            }`}
          >
            <span className="relative z-10">All Articles</span>
            {!category && (
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
            )}
          </button>
          
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => filterByCategory(cat.slug)}
              className={`group relative px-6 py-3 rounded-full font-semibold transition-all duration-200 ${
                category === cat.slug
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/50'
                  : 'bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border border-white/20 dark:border-slate-700/50 text-slate-700 dark:text-slate-300 '
              }`}
            >
              <span className="relative z-10">{cat.name}</span>
              {category === cat.slug && (
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
              )}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-32">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-900 rounded-full" />
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0" />
            </div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-32">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-500/20 backdrop-blur-xl mb-6">
              <TrendingUp className="w-10 h-10 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-xl text-slate-600 dark:text-slate-400">No articles found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {posts.map((post, index) => (
              <Link
                key={post.id}
                href={`/${locale}/resources/blog/${post.slug}`}
                className="group relative"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Glass card with gradient border */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-pink-600 rounded-2xl opacity-0 group-hover:opacity-100 blur transition duration-500" />
                
                <div className="relative h-full bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-slate-700/50 overflow-hidden transition-all duration-500 group-hover:scale-[1.02] group-hover:shadow-lg">
                  {/* Featured Image with Overlay */}
                  <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-blue-500/20 to-indigo-500/20">
                    {post.featured_image ? (
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover group- transition-transform duration-700"
                      />
                    ) : (
                      <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                        {/* Dynamic gradient background based on post ID */}
                        <div 
                          className="absolute inset-0 opacity-60"
                          style={{
                            background: `linear-gradient(135deg, 
                              hsl(${(post.id * 137.5) % 360}, 70%, 50%) 0%, 
                              hsl(${(post.id * 137.5 + 60) % 360}, 70%, 60%) 50%,
                              hsl(${(post.id * 137.5 + 120) % 360}, 70%, 50%) 100%)`
                          }}
                        />
                        {/* Animated mesh gradient overlay */}
                        <div className="absolute inset-0 opacity-30">
                          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/40 via-transparent to-transparent" />
                          <div className="absolute bottom-0 right-0 w-3/4 h-3/4 bg-gradient-to-tl from-black/20 via-transparent to-transparent" />
                        </div>
                        {/* Geometric pattern */}
                        <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <pattern id={`pattern-${post.id}`} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                              <circle cx="20" cy="20" r="2" fill="white" />
                              <circle cx="0" cy="0" r="2" fill="white" />
                              <circle cx="40" cy="40" r="2" fill="white" />
                            </pattern>
                          </defs>
                          <rect width="100%" height="100%" fill={`url(#pattern-${post.id})`} />
                        </svg>
                        {/* Icon based on category */}
                        <div className="relative z-10 flex flex-col items-center gap-4">
                          {post.category_name?.toLowerCase().includes('practice') ? (
                            <svg className="w-24 h-24 text-white/90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          ) : post.category_name?.toLowerCase().includes('tutorial') ? (
                            <svg className="w-24 h-24 text-white/90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          ) : post.category_name?.toLowerCase().includes('update') ? (
                            <svg className="w-24 h-24 text-white/90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          ) : (
                            <svg className="w-24 h-24 text-white/90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                          )}
                          <div className="text-center">
                            <div className="text-2xl font-black text-white/90 tracking-tight">
                              {post.title.split(' ').slice(0, 3).join(' ')}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    
                    {/* Category Badge */}
                    {post.category_name && (
                      <div className="absolute top-4 left-4">
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-white/20 text-sm font-bold text-slate-900 dark:text-white shadow-lg">
                          {post.category_name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-4">
                    <h2 className="text-2xl font-black text-slate-900 dark:text-white group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-indigo-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-200 line-clamp-2">
                      {post.title}
                    </h2>
                    
                    <p className="text-slate-600 dark:text-slate-400 line-clamp-3 leading-relaxed">
                      {post.excerpt}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center justify-between pt-4 border-t border-slate-200/50 dark:border-slate-700/50">
                      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                        <User className="w-4 h-4" />
                        <span className="font-medium">{post.author_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        {post.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag.id}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-500/10 to-indigo-500/10 text-blue-700 dark:text-blue-300 border border-blue-200/50 dark:border-blue-700/50"
                          >
                            <Tag className="w-3 h-3" />
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Read More Arrow */}
                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold pt-2 group-hover:gap-4 transition-all">
                      <span>Read Article</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-3">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
              <Link
                key={p}
                href={`?${new URLSearchParams({ ...Object.fromEntries(searchParams.entries()), page: p.toString() }).toString()}`}
                className={`relative group px-6 py-3 rounded-full font-bold transition-all duration-200 ${
                  p === pagination.page
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/50'
                    : 'bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border border-white/20 dark:border-slate-700/50 text-slate-700 dark:text-slate-300 '
                }`}
              >
                {p}
                {p === pagination.page && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
                )}
              </Link>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}
